import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Badge from "@mui/material/Badge";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import AccountCircle from "@mui/icons-material/AccountCircle";
import NotificationsIcon from "@mui/icons-material/Notifications";
import {useLogoutMutation, useSessionQuery} from "../app/api/api";
import {useSnackbar} from "notistack";
import {Link as RouterLink, useNavigate} from "react-router-dom";
import LanguageIcon from "@mui/icons-material/Language";
import LogoutIcon from "@mui/icons-material/Logout";
import PasswordIcon from "@mui/icons-material/Password";
import {Button, Divider, Drawer, Link, ListItemIcon, ListItemText, Typography} from "@mui/material";
import {ReactComponent as Logo} from "../assets/logo.svg";
import {useTranslation} from "react-i18next";
import {NavigationBar} from "./NavigationBar";

const DRAWER_WIDTH = 240;

export default function MainAppBar() {
    const {data: session} = useSessionQuery();
    const [logout] = useLogoutMutation();
    const {enqueueSnackbar} = useSnackbar();
    const navigate = useNavigate();
    const {t, i18n} = useTranslation();

    const handleLogout = async () => {
        const data = await logout().unwrap();
        if (data.Code === "loggedOut") {
            enqueueSnackbar("Logged out", {variant: "success"});
        } else {
            enqueueSnackbar(data.Message, {variant: "error"});
        }
        navigate("/");
    };

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const isMenuOpen = Boolean(anchorEl);

    const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const renderMenu = (
        <Menu
            anchorEl={anchorEl}
            anchorOrigin={{
                vertical: "top",
                horizontal: "right",
            }}
            keepMounted
            transformOrigin={{
                vertical: "top",
                horizontal: "right",
            }}
            open={isMenuOpen}
            onClose={handleMenuClose}
        >
            {i18n.language === "de" ? (
                <MenuItem component={"a"} href={"?lng=en"} key={"en"} data-test="languageMenuItem">
                    <ListItemIcon>
                        <LanguageIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>English</ListItemText>
                </MenuItem>
            ) : (
                <MenuItem component={"a"} href={"?lng=de"} key={"de"} data-test="languageMenuItem">
                    <ListItemIcon>
                        <LanguageIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Deutsch</ListItemText>
                </MenuItem>
            )}
            <MenuItem
                component={RouterLink}
                to="/password"
                onClick={handleMenuClose}
                data-test="updatePasswordMenuItem"
            >
                <ListItemIcon>
                    <PasswordIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t("action_updatePassword")}</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout} data-test="logoutMenuItem">
                <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t("action_logout")}</ListItemText>
            </MenuItem>
        </Menu>
    );

    return (
        <>
            <AppBar position="fixed" sx={{zIndex: (theme) => theme.zIndex.drawer + 1}}>
                <Toolbar>
                    <Link
                        color="inherit"
                        component={RouterLink}
                        to="/"
                        sx={{display: {xs: "none", sm: "flex"}, alignItems: "center"}}
                        underline="hover"
                    >
                        <Logo height={24} />
                        <Typography variant="h6" sx={{ml: 2}}>
                            Management
                        </Typography>
                    </Link>
                    <Box sx={{flexGrow: 1}} />
                    <Box sx={{display: {xs: "none", md: "flex"}}}>
                        <IconButton
                            size="large"
                            aria-label="notifications"
                            color="inherit"
                            data-test="notificationsButton"
                        >
                            <Badge badgeContent={0} color="error">
                                <NotificationsIcon />
                            </Badge>
                        </IconButton>
                        <Button color="inherit" onClick={handleProfileMenuOpen} data-test="profileButton">
                            <AccountCircle sx={{mr: 1}} /> {`${session?.firstName} ${session?.lastName}`}
                        </Button>
                    </Box>
                </Toolbar>
            </AppBar>
            {renderMenu}
            <Drawer
                variant="permanent"
                sx={{
                    width: DRAWER_WIDTH,
                    flexShrink: 0,
                    [`& .MuiDrawer-paper`]: {
                        width: DRAWER_WIDTH,
                        boxSizing: "border-box",
                    },
                }}
            >
                <Toolbar />
                <Box sx={{overflow: "auto"}}>
                    <NavigationBar />
                </Box>
            </Drawer>
        </>
    );
}
